import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    BadgeDollarSign,
    Coins,
    Gem,
    Wallet,
    MessageSquareText,
    Activity,
    ShieldAlert,
    Boxes,
    RefreshCw,
    ExternalLink,
    Landmark,
} from "lucide-react";

import blockchainApi from "../../../api/blockchainApi";
import promosApiBundle from "../../../api/promosApi";
import userApi from "../../../api/usersApi";
import supportApi from "../../../api/supportApi";
import awardApi from "../../../api/awardApi";

const { promosApi } = promosApiBundle;

// -----------------------------
// utils
// -----------------------------
function cn(...classes) {
    return classes.filter(Boolean).join(" ");
}

function formatNumber(n) {
    if (n === null || n === undefined) return "—";
    const num = Number(n);
    if (Number.isNaN(num)) return "—";
    return num.toLocaleString("en-US");
}

function formatToken(n, symbol = "") {
    if (n === null || n === undefined) return "—";
    const num = Number(n);
    if (Number.isNaN(num)) return "—";
    const s = num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    return symbol ? `${s} ${symbol}` : s;
}

function formatPct(n) {
    if (n === null || n === undefined) return "—";
    const num = Number(n);
    if (Number.isNaN(num)) return "—";
    return `${num.toFixed(1)}%`;
}

function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}

function shortAddr(addr) {
    return addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—";
}

function timeAgo(ts) {
    if (!ts) return "—";
    const diff = Date.now() - new Date(ts).getTime();
    if (Number.isNaN(diff)) return "—";
    const m = Math.floor(diff / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// В tokenType у on-chain промо может лежать адрес контракта
function tokenLabel(t) {
    if (!t) return "—";
    const s = String(t);
    return s.startsWith("0x") && s.length > 20 ? shortAddr(s) : s.toUpperCase();
}

// -----------------------------
// SVG Mini Sparkline
// -----------------------------
function Sparkline({ data = [], height = 34, strokeWidth = 2, className }) {
    const w = 120;
    const h = height;
    const pad = 4;

    const points = useMemo(() => {
        if (!data?.length) return [];
        const min = Math.min(...data);
        const max = Math.max(...data);
        const span = max - min || 1;

        return data.map((v, i) => {
            const x = pad + (i / (data.length - 1 || 1)) * (w - pad * 2);
            const y = pad + (1 - (v - min) / span) * (h - pad * 2);
            return { x, y };
        });
    }, [data, h]);

    const d = useMemo(() => {
        if (!points.length) return "";
        return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    }, [points]);

    const area = useMemo(() => {
        if (!points.length) return "";
        const first = points[0];
        const last = points[points.length - 1];
        return `${d} L ${last.x} ${h - 2} L ${first.x} ${h - 2} Z`;
    }, [d, points, h]);

    if (!points.length) return null;

    return (
        <svg
            width={w}
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            className={cn("opacity-95", className)}
        >
            <defs>
                <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="rgba(255,255,255,0.22)" />
                    <stop offset="1" stopColor="rgba(255,255,255,0.00)" />
                </linearGradient>
            </defs>

            <path d={area} fill="url(#sparkFill)" />
            <path d={d} fill="none" stroke="rgba(255,255,255,0.70)" strokeWidth={strokeWidth} />
        </svg>
    );
}

// -----------------------------
// UI shells
// -----------------------------
function WidgetShell({ title, subtitle, right, children, className }) {
    return (
        <section
            className={cn(
                "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl",
                "shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]",
                "transition-all duration-300 hover:bg-white/[0.06]",
                className
            )}
        >
            <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/5 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-white/5 blur-2xl" />

            {(title || right) && (
                <header className="flex items-start justify-between gap-4 p-4 border-b border-white/10">
                    <div className="min-w-0">
                        {title && (
                            <h3 className="text-[15px] font-semibold text-white/90 leading-5 truncate">
                                {title}
                            </h3>
                        )}
                        {subtitle && (
                            <p className="mt-1 text-[12px] text-white/50 leading-4 truncate">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {right && <div className="shrink-0">{right}</div>}
                </header>
            )}

            <div className="p-4">{children}</div>
        </section>
    );
}

function Pill({ children, tone = "neutral", icon: Icon }) {
    const cls =
        tone === "ok"
            ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-200"
            : tone === "warn"
                ? "border-amber-400/25 bg-amber-400/10 text-amber-200"
                : tone === "bad"
                    ? "border-rose-400/25 bg-rose-400/10 text-rose-200"
                    : "border-white/10 bg-white/5 text-white/70";
    return (
        <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[12px]", cls)}>
      {Icon ? <Icon className="w-3.5 h-3.5 opacity-90" /> : null}
            {children}
    </span>
    );
}

function KpiCard({ icon: Icon, label, value, sub, spark, onClick }) {
    return (
        <div
            onClick={onClick}
            className={cn(
                "relative overflow-hidden rounded-2xl border border-white/10 backdrop-blur-xl bg-white/[0.04]",
                "shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]",
                "p-4 transition hover:bg-white/[0.06]",
                onClick && "cursor-pointer"
            )}
        >
            <div className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/5 blur-2xl" />

            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[12px] text-white/55">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl border border-white/10 bg-white/5">
              {Icon ? <Icon className="w-4 h-4 text-white/85" /> : null}
            </span>
                        <span className="truncate">{label}</span>
                    </div>

                    <div className="mt-2 text-[20px] sm:text-[22px] font-semibold text-white/90 leading-6 truncate">
                        {value}
                    </div>

                    {sub && <div className="mt-1 text-[12px] text-white/45 truncate">{sub}</div>}
                </div>

                {spark?.length ? (
                    <div className="shrink-0">
                        <Sparkline data={spark} />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function Table({ columns, rows, rowKey = (r, i) => i, emptyText = "No data" }) {
    return (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-auto">
                <table className="min-w-full text-left text-[13px]">
                    <thead className="bg-white/5 text-white/70">
                    <tr>
                        {columns.map((c) => (
                            <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">
                                {c.title}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="text-white/80">
                    {rows.length === 0 ? (
                        <tr>
                            <td className="px-4 py-4 text-white/50" colSpan={columns.length}>
                                {emptyText}
                            </td>
                        </tr>
                    ) : (
                        rows.map((r, i) => (
                            <tr key={rowKey(r, i)} className="border-t border-white/10 hover:bg-white/[0.04]">
                                {columns.map((c) => (
                                    <td key={c.key} className="px-4 py-3 whitespace-nowrap">
                                        {typeof c.render === "function" ? c.render(r) : r[c.dataIndex]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// -----------------------------
// data
// -----------------------------
const OPEN_TICKET_STATUSES = ["OPEN", "IN_PROGRESS", "WAITING_SUPPORT", "WAITING_USER"];

function useDashboardData() {
    const [state, setState] = useState({ loading: true });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: !s.dash }));

        const results = await Promise.allSettled([
            blockchainApi.getDashboard().then((r) => r.data),
            blockchainApi.getClaims(1, 200).then((r) => r.data),
            blockchainApi.getDeposits(1, 6).then((r) => r.data),
            promosApi.getPaginatedPromos({ page: 1, limit: 6 }),
            promosApi.getPaginatedPromos({ page: 1, limit: 1, status: "ACTIVE" }),
            promosApi.getPaginatedPromos({ page: 1, limit: 1, status: "PENDING" }),
            promosApi.getPaginatedPromos({ page: 1, limit: 1, status: "COMPLETED" }),
            userApi.getAll(),
            supportApi.getTickets(),
        ]);

        const val = (i, fallback = null) =>
            results[i].status === "fulfilled" ? results[i].value : fallback;

        // Победители: winnersCount на промо не ведётся — реальное число выданных
        // наград отдаёт award-service (total из GET /awards/:promoId).
        const latest = val(3);
        const winnersByPromo = {};
        const latestList = latest?.data || [];
        if (latestList.length) {
            const counts = await Promise.allSettled(
                latestList.map((p) => awardApi.getByPromo(p.id, 1, 1))
            );
            counts.forEach((c, i) => {
                if (c.status === "fulfilled" && c.value && typeof c.value.total === "number") {
                    winnersByPromo[latestList[i].id] = c.value.total;
                }
            });
        }

        setState({
            loading: false,
            dash: val(0),
            claims: val(1),
            deposits: val(2),
            promosLatest: latest,
            winnersByPromo,
            promosActive: val(4),
            promosPending: val(5),
            promosCompleted: val(6),
            users: val(7, []),
            tickets: val(8, []),
            loadedAt: new Date(),
        });
    }, []);

    useEffect(() => { load(); }, [load]);

    return { ...state, reload: load };
}

// -----------------------------
// page
// -----------------------------
export default function AdminDashboard() {
    const navigate = useNavigate();
    const {
        loading, dash, claims, deposits,
        promosLatest, promosActive, promosPending, promosCompleted,
        users, tickets, loadedAt, reload, winnersByPromo = {},
    } = useDashboardData();

    // ── деривативы из живых данных ──
    const derived = useMemo(() => {
        const claimItems = claims?.items || [];
        const claimsTotal = claims?.total ?? claimItems.length;

        // Выплачено по токенам (по загруженной странице клеймов)
        const paidByToken = {};
        let gasSpentEth = 0;
        for (const c of claimItems) {
            const sym = c.tokenSymbol || "?";
            paidByToken[sym] = (paidByToken[sym] || 0) + (Number(c.amountHuman) || 0);
            gasSpentEth += Number(c.gasCostEth) || 0;
        }
        const paidTop = Object.entries(paidByToken).sort((a, b) => b[1] - a[1]);
        const paidApprox = claimsTotal > claimItems.length;

        // Клеймы по дням — последние 14 дней, для спарклайна
        const byDay = new Array(14).fill(0);
        const now = Date.now();
        for (const c of claimItems) {
            const d = Math.floor((now - new Date(c.claimedAt).getTime()) / 86400000);
            if (d >= 0 && d < 14) byDay[13 - d] += 1;
        }

        const contracts = dash?.contracts || [];
        const pool = contracts.find((c) => c.name === "GiveawayPool");
        const platform = contracts.find((c) => c.name === "PlatformWallet");
        const poolTokens = (pool?.tokenBalances || []).filter((t) => Number(t.balance) > 0);
        const platformTokens = (platform?.tokenBalances || []).filter((t) => Number(t.balance) > 0);
        const feesTop = [...platformTokens].sort((a, b) => Number(b.balance) - Number(a.balance))[0];

        const ticketList = Array.isArray(tickets) ? tickets : tickets?.items || tickets?.data || [];
        const openTickets = ticketList.filter((t) => OPEN_TICKET_STATUSES.includes(String(t.status).toUpperCase()));

        const userList = Array.isArray(users) ? users : [];
        const users7d = userList.filter(
            (u) => u.createdAt && now - new Date(u.createdAt).getTime() < 7 * 86400000
        ).length;

        const byStatus = dash?.promos?.byStatus || {};

        return {
            claimItems, claimsTotal, paidTop, paidApprox, gasSpentEth, claimSpark: byDay,
            pool, platform, poolTokens, platformTokens, feesTop,
            ticketList, openTickets, userList, users7d, byStatus,
        };
    }, [claims, dash, tickets, users]);

    const chainName = dash?.network?.chainId === 42161 ? "Arbitrum One" : dash?.network?.chainId ? `Chain ${dash.network.chainId}` : null;
    const isMainnet = dash?.network?.chainId === 42161;
    const txLink = (hash) => isMainnet && hash ? `https://arbiscan.io/tx/${hash}` : null;

    const lastClaim = derived.claimItems[0];

    if (loading) {
        return (
            <div className="w-full min-h-screen p-3 sm:p-4">
                <div className="fixed inset-0 -z-10 bg-[#070A12]" />
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-[110px] rounded-2xl border border-white/10 bg-white/[0.04] animate-pulse" />
                    ))}
                </div>
                <div className="mt-4 h-[320px] rounded-2xl border border-white/10 bg-white/[0.04] animate-pulse" />
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen p-3 sm:p-4">
            {/* background */}
            <div className="fixed inset-0 -z-10 bg-[#070A12]" />
            <div className="fixed inset-0 -z-10 opacity-60">
                <div className="absolute -top-24 -left-24 h-[380px] w-[380px] rounded-full bg-indigo-500/20 blur-[90px]" />
                <div className="absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-fuchsia-500/20 blur-[100px]" />
                <div className="absolute bottom-0 left-1/3 h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-[110px]" />
            </div>

            {/* header */}
            <div className="mb-4">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)] overflow-hidden">
                    <div className="p-4 sm:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                            <div className="text-[18px] sm:text-[20px] font-semibold text-white/90">
                                Dashboard
                            </div>
                            <div className="mt-1 text-[13px] text-white/55">
                                {chainName ? `${chainName} • block ${formatNumber(dash?.network?.blockNumber)} • ${dash?.network?.gasPrice || ""}` : "Network offline"}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {dash?.contractPaused ? (
                                <Pill tone="bad" icon={ShieldAlert}>Contract PAUSED</Pill>
                            ) : dash ? (
                                <Pill tone="ok" icon={Activity}>Contract live</Pill>
                            ) : (
                                <Pill tone="warn" icon={ShieldAlert}>No chain data</Pill>
                            )}

                            <button
                                type="button"
                                onClick={reload}
                                className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:bg-white/10 transition"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                {loadedAt ? loadedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "Refresh"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <KpiCard
                    icon={Users}
                    label="Users"
                    value={formatNumber(derived.userList.length)}
                    sub={`+${formatNumber(derived.users7d)} last 7 days`}
                    onClick={() => navigate("/users")}
                />
                <KpiCard
                    icon={Boxes}
                    label="Promos"
                    value={formatNumber(promosLatest?.total)}
                    sub={`${formatNumber(promosActive?.total)} active • ${formatNumber(promosPending?.total)} pending • ${formatNumber(promosCompleted?.total)} completed`}
                    onClick={() => navigate("/promos")}
                />
                <KpiCard
                    icon={Landmark}
                    label="Prize pool (on-chain)"
                    value={
                        derived.poolTokens.length
                            ? formatToken(derived.poolTokens[0].balance, derived.poolTokens[0].symbol)
                            : "0"
                    }
                    sub={
                        derived.poolTokens.length > 1
                            ? derived.poolTokens.slice(1).map((t) => formatToken(t.balance, t.symbol)).join(" • ")
                            : "GiveawayPool balance"
                    }
                    onClick={() => navigate("/blockchain")}
                />
                <KpiCard
                    icon={BadgeDollarSign}
                    label="Fees earned"
                    value={derived.feesTop ? formatToken(derived.feesTop.balance, derived.feesTop.symbol) : "0"}
                    sub={derived.platform ? `Platform wallet ${shortAddr(derived.platform.address)}` : "—"}
                />
                <KpiCard
                    icon={Gem}
                    label="On-chain claims"
                    value={formatNumber(derived.claimsTotal)}
                    sub={lastClaim ? `last ${timeAgo(lastClaim.claimedAt)}` : "No claims yet"}
                    spark={derived.claimSpark}
                />
                <KpiCard
                    icon={Coins}
                    label="Paid out to winners"
                    value={
                        derived.paidTop.length
                            ? `${derived.paidApprox ? "≈" : ""}${formatToken(derived.paidTop[0][1], derived.paidTop[0][0])}`
                            : "0"
                    }
                    sub={
                        derived.paidTop.length > 1
                            ? derived.paidTop.slice(1).map(([s, v]) => formatToken(v, s)).join(" • ")
                            : `${formatNumber(derived.claimItems.length)} claims counted`
                    }
                />
                <KpiCard
                    icon={Wallet}
                    label="Funded promos"
                    value={formatNumber(deposits?.total)}
                    sub={deposits?.items?.[0] ? `last: ${deposits.items[0].title || `#${deposits.items[0].promoId}`}` : "—"}
                    onClick={() => navigate("/blockchain")}
                />
                <KpiCard
                    icon={MessageSquareText}
                    label="Support open"
                    value={formatNumber(derived.openTickets.length)}
                    sub={`${formatNumber(derived.ticketList.length)} tickets total`}
                    onClick={() => navigate("/chat")}
                />
            </div>

            {/* Main layout */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Left */}
                <div className="lg:col-span-8 space-y-4">
                    <WidgetShell
                        title="Latest promos"
                        subtitle="Newest first"
                        right={
                            <button
                                type="button"
                                onClick={() => navigate("/promos")}
                                className="text-[12px] text-white/60 hover:text-white transition"
                            >
                                All promos →
                            </button>
                        }
                    >
                        <Table
                            columns={[
                                { key: "id", title: "ID", render: (r) => <span className="text-white/60">{r.id}</span> },
                                {
                                    key: "promo",
                                    title: "Promo",
                                    render: (r) => (
                                        <div className="min-w-[180px] max-w-[260px]">
                                            <div className="text-[13px] font-semibold text-white/90 truncate">
                                                {r.title || `Promo #${r.id}`}
                                            </div>
                                            <div className="text-[11px] text-white/45">{timeAgo(r.createdAt)}</div>
                                        </div>
                                    ),
                                },
                                {
                                    key: "status",
                                    title: "Status",
                                    render: (r) => (
                                        <Pill
                                            tone={r.status === "ACTIVE" ? "ok" : r.status === "PENDING" ? "warn" : "neutral"}
                                        >
                                            {r.status}
                                        </Pill>
                                    ),
                                },
                                {
                                    key: "budget",
                                    title: "Budget",
                                    render: (r) => (
                                        <span className="font-semibold">
                                            {formatToken(r.totalAmount, tokenLabel(r.tokenType))}
                                        </span>
                                    ),
                                },
                                {
                                    key: "winners",
                                    title: "Winners",
                                    render: (r) => {
                                        const max = Number(r.prizePlacesCount) || 0;
                                        const won = winnersByPromo[r.id] ?? (Number(r.winnersCount) || 0);
                                        const pct = max > 0 ? (won / max) * 100 : 0;
                                        return (
                                            <div className="min-w-[150px]">
                                                <div className="flex items-center justify-between text-[12px] text-white/60 mb-1">
                                                    <span>{formatNumber(won)} / {formatNumber(max)}</span>
                                                    <span className="text-white/85 font-semibold">{formatPct(pct)}</span>
                                                </div>
                                                <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full bg-white/45"
                                                        style={{ width: `${clamp(pct, 0, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    },
                                },
                                { key: "participants", title: "Participants", render: (r) => formatNumber(r.participantsCount) },
                            ]}
                            rows={promosLatest?.data || []}
                            rowKey={(r) => r.id}
                            emptyText="No promos"
                        />
                    </WidgetShell>

                    <WidgetShell
                        title="Recent on-chain claims"
                        subtitle="Winners receiving prizes from GiveawayPool"
                    >
                        <div className="space-y-2">
                            {derived.claimItems.length === 0 && (
                                <div className="text-[13px] text-white/50">No claims yet</div>
                            )}
                            {derived.claimItems.slice(0, 8).map((c) => {
                                const link = txLink(c.txHash);
                                return (
                                    <div
                                        key={c.txHash}
                                        className="rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition p-3 flex items-start justify-between gap-3"
                                    >
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center shrink-0">
                                                <Gem className="w-5 h-5 text-white/85" />
                                            </div>

                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <Pill tone="ok">CLAIM</Pill>
                                                    <div className="text-[12px] text-white/50 truncate">
                                                        promo #{c.promoId}
                                                    </div>
                                                </div>
                                                <div className="mt-1 text-[13px] text-white/85 truncate">
                                                    {shortAddr(c.winnerAddress)} received{" "}
                                                    <span className="font-semibold">
                                                        {formatToken(c.amountHuman, c.tokenSymbol || "")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="shrink-0 flex flex-col items-end gap-1">
                                            <span className="text-[12px] text-white/45">{timeAgo(c.claimedAt)}</span>
                                            {link && (
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 text-[12px] text-white/50 hover:text-white transition"
                                                >
                                                    tx <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </WidgetShell>
                </div>

                {/* Right */}
                <div className="lg:col-span-4 space-y-4">
                    <WidgetShell
                        title="Contracts"
                        subtitle="Live balances"
                        right={
                            <button
                                type="button"
                                onClick={() => navigate("/blockchain")}
                                className="text-[12px] text-white/60 hover:text-white transition"
                            >
                                Blockchain →
                            </button>
                        }
                    >
                        <div className="space-y-2">
                            {(dash?.contracts || []).map((c) => (
                                <div key={c.name} className="rounded-xl border border-white/10 bg-white/5 p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="text-[13px] font-semibold text-white/85">{c.name}</div>
                                        <div className="text-[11px] text-white/40">{shortAddr(c.address)}</div>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {(c.tokenBalances || [])
                                            .filter((t) => Number(t.balance) > 0)
                                            .map((t) => (
                                                <Pill key={t.symbol}>{formatToken(t.balance, t.symbol)}</Pill>
                                            ))}
                                        {(c.tokenBalances || []).every((t) => Number(t.balance) <= 0) && (
                                            <span className="text-[12px] text-white/40">empty</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {!dash && <div className="text-[13px] text-white/50">Chain data unavailable</div>}
                        </div>
                    </WidgetShell>

                    <WidgetShell title="Blockchain promos" subtitle="On-chain lifecycle">
                        <div className="space-y-2">
                            {Object.keys(derived.byStatus).length === 0 && (
                                <div className="text-[13px] text-white/50">No on-chain promos</div>
                            )}
                            {Object.entries(derived.byStatus).map(([status, count]) => (
                                <div key={status} className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                                    <Pill
                                        tone={
                                            status === "funded" ? "ok"
                                                : status === "claiming" ? "ok"
                                                    : status === "withdrawn" ? "neutral"
                                                        : "warn"
                                        }
                                    >
                                        {status}
                                    </Pill>
                                    <span className="text-white/90 font-semibold">{formatNumber(count)}</span>
                                </div>
                            ))}

                            {(deposits?.items || []).slice(0, 4).map((d) => (
                                <div key={d.promoId} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-[13px] text-white/85 truncate">
                                            {d.title || `Promo #${d.promoId}`}
                                        </div>
                                        <span className="text-[12px] text-white/45 shrink-0">{timeAgo(d.depositedAt)}</span>
                                    </div>
                                    <div className="mt-1 text-[12px] text-white/50">
                                        deposit {formatToken(d.totalAmount, "")} • {d.blockchainStatus}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </WidgetShell>

                    <WidgetShell
                        title="Support inbox"
                        subtitle="Latest tickets"
                        right={
                            <button
                                type="button"
                                onClick={() => navigate("/chat")}
                                className="text-[12px] text-white/60 hover:text-white transition"
                            >
                                Open →
                            </button>
                        }
                    >
                        <div className="space-y-2">
                            {derived.ticketList.length === 0 && (
                                <div className="text-[13px] text-white/50">No tickets</div>
                            )}
                            {derived.ticketList.slice(0, 5).map((t) => {
                                const open = OPEN_TICKET_STATUSES.includes(String(t.status).toUpperCase());
                                return (
                                    <div
                                        key={t.id}
                                        className="rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition p-3 flex items-start gap-3"
                                    >
                                        <div className="w-10 h-10 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center shrink-0">
                                            <MessageSquareText className="w-5 h-5 text-white/85" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="text-[13px] font-semibold text-white/85 truncate">
                                                    {t.subject || `Ticket #${t.id}`}
                                                </div>
                                                <Pill tone={open ? "warn" : "ok"}>{t.status}</Pill>
                                            </div>
                                            <div className="mt-1 text-[12px] text-white/50 truncate">
                                                {t.username || `user ${t.userId}`} • {timeAgo(t.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </WidgetShell>

                    <WidgetShell title="Quick insights" subtitle="Computed from live data">
                        <div className="space-y-2 text-[13px] text-white/70">
                            <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                                <span>Avg claim size</span>
                                <span className="text-white/90 font-semibold">
                                    {derived.paidTop.length && derived.claimItems.length
                                        ? formatToken(derived.paidTop[0][1] / derived.claimItems.filter(c => (c.tokenSymbol || "?") === derived.paidTop[0][0]).length, derived.paidTop[0][0])
                                        : "—"}
                                </span>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                                <span>Gas spent on claims</span>
                                <span className="text-white/90 font-semibold">
                                    {derived.gasSpentEth ? `${derived.gasSpentEth.toFixed(6)} ETH` : "—"}
                                </span>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between">
                                <span>Ticket resolution</span>
                                <span className="text-white/90 font-semibold">
                                    {derived.ticketList.length
                                        ? formatPct(((derived.ticketList.length - derived.openTickets.length) / derived.ticketList.length) * 100)
                                        : "—"}
                                </span>
                            </div>
                        </div>
                    </WidgetShell>
                </div>
            </div>
        </div>
    );
}
